/**
 * Couleur de marque (accent) personnalisable par l'utilisateur.
 *
 * Le cockpit n'a qu'UNE couleur thématique de marque — le token `--color-mark`
 (#3b82f6 par défaut — le bleu, Kayla 12-09-2026), décliné en `--color-mark-hover` / `--color-mark-fg` et
 * dupliqué dans `--cockpit-corner-color` (triangles de coin des panneaux). Tout
 * le reste de « l'orange » du cockpit (boutons accent, onglet actif, sélections,
 * cases cochées, badges, graphes…) résout `var(--color-mark)` via les
 * utilitaires Tailwind `*-mark`. Cf `src/styles/globals.css` + `docs/theming.md`.
 *
 * Conséquence : poser ces 4 CSS vars en inline sur <html> recolore TOUT le
 * cockpit en direct, sans toucher au CSS — exactement le « éditeur de couleurs
 * custom » que la doc theming anticipe.
 *
 * Ce module est pur (les helpers de couleur n'ont aucune dépendance DOM ;
 * seul `applyBrandColor` touche un élément). La persistance vit dans
 * `brandColorAtom` (core/state/shell.ts) ; l'application sur <html> est faite
 * par `ThemeProvider`.
 */

/** Couleur de marque par défaut — DOIT rester égale à `--color-mark` (theme.css) : le bleu de
 *  la palette « Joyaux » choisie par Kayla le 02-11-2026. */
export const DEFAULT_BRAND = "#4d7cff";

export interface BrandPreset {
  id: string;
  hex: string;
  labelKey: string;
}

/** Presets proposés dans le picker. Le 1er = défaut (le bleu, Kayla 12-09-2026 :
 *  « tu mets du bleu à la place du vert ») ; le vert Dozka du 10-09, l'or de RishFans et le
 *  rouge historique restent des choix. Depuis le 02-11-2026 (palette « Joyaux »), le bleu,
 *  le vert et l'ambre sont ceux du thème — plus les teintes Tailwind, que Kayla trouvait
 *  « par défaut ». */
export const BRAND_PRESETS: BrandPreset[] = [
  { id: "bleu", hex: DEFAULT_BRAND, labelKey: "shell.themePicker.accent.presets.bleu" },
  { id: "or", hex: "#e9c46a", labelKey: "shell.themePicker.accent.presets.or" },
  { id: "dozka", hex: "#71fe94", labelKey: "shell.themePicker.accent.presets.dozka" },
  { id: "rouge", hex: "#ed2e1c", labelKey: "shell.themePicker.accent.presets.rouge" },
  { id: "orange", hex: "#f97316", labelKey: "shell.themePicker.accent.presets.orange" },
  { id: "ambre", hex: "#f5b544", labelKey: "shell.themePicker.accent.presets.ambre" },
  { id: "rose", hex: "#ec4899", labelKey: "shell.themePicker.accent.presets.rose" },
  { id: "violet", hex: "#8b5cf6", labelKey: "shell.themePicker.accent.presets.violet" },
  { id: "cyan", hex: "#06b6d4", labelKey: "shell.themePicker.accent.presets.cyan" },
  { id: "vert", hex: "#1fbf8f", labelKey: "shell.themePicker.accent.presets.vert" },
  { id: "lime", hex: "#84cc16", labelKey: "shell.themePicker.accent.presets.lime" },
];

/** Les 4 CSS vars qui portent la couleur de marque. */
const BRAND_VARS = [
  "--color-mark",
  "--color-mark-hover",
  "--color-mark-fg",
  "--cockpit-corner-color",
] as const;

interface Rgb {
  r: number;
  g: number;
  b: number;
}

/**
 * Normalise un code hex en `#rrggbb` minuscule. Accepte `#rgb`, `rgb`,
 * `#rrggbb`, `rrggbb` (avec/sans `#`, casse libre). Renvoie `null` si invalide.
 */
export function normalizeHex(input: string): string | null {
  if (typeof input !== "string") return null;
  const s = input.trim().replace(/^#/, "").toLowerCase();
  if (/^[0-9a-f]{3}$/.test(s)) {
    return "#" + s.split("").map((c) => c + c).join("");
  }
  if (/^[0-9a-f]{6}$/.test(s)) return "#" + s;
  return null;
}

function hexToRgb(hex: string): Rgb {
  // hex supposé normalisé (#rrggbb).
  const n = parseInt(hex.slice(1), 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

function clamp255(v: number): number {
  return Math.max(0, Math.min(255, v));
}

function rgbToHex({ r, g, b }: Rgb): string {
  const h = (v: number) => clamp255(Math.round(v)).toString(16).padStart(2, "0");
  return "#" + h(r) + h(g) + h(b);
}

/** Luminance relative WCAG (0 = noir, 1 = blanc). */
export function relativeLuminance(hex: string): number {
  const { r, g, b } = hexToRgb(hex);
  const lin = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

/**
 * Couleur de texte à poser SUR la couleur de marque : blanc ou quasi-noir,
 * pour rester lisible quelle que soit la couleur choisie.
 *
 * On utilise la luminosité PERÇUE (YIQ) avec un seuil qui penche vers le blanc
 * (150) : c'est la convention « texte blanc sur couleur de marque saturée »
 * (rouge, bleu, violet, rose…) ; seules les teintes vraiment claires (ambre,
 * citron) passent en texte sombre. Le ratio de contraste WCAG pur choisirait
 * parfois le noir sur un rouge saturé comme #ED2E1C — contre-intuitif et
 * contraire au défaut historique (`--color-mark-fg: #ffffff`).
 */
export function contrastFg(hex: string): string {
  const norm = normalizeHex(hex);
  if (!norm) return "#ffffff";
  const { r, g, b } = hexToRgb(norm);
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq >= 150 ? "#0a0a0a" : "#ffffff";
}

function rgbToHsl({ r, g, b }: Rgb): { h: number; s: number; l: number } {
  const rr = r / 255;
  const gg = g / 255;
  const bb = b / 255;
  const max = Math.max(rr, gg, bb);
  const min = Math.min(rr, gg, bb);
  const l = (max + min) / 2;
  const d = max - min;
  let h = 0;
  let s = 0;
  if (d !== 0) {
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case rr:
        h = (gg - bb) / d + (gg < bb ? 6 : 0);
        break;
      case gg:
        h = (bb - rr) / d + 2;
        break;
      default:
        h = (rr - gg) / d + 4;
    }
    h /= 6;
  }
  return { h, s, l };
}

function hslToRgb(h: number, s: number, l: number): Rgb {
  if (s === 0) {
    const v = l * 255;
    return { r: v, g: v, b: v };
  }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const hue = (t: number) => {
    let tt = t;
    if (tt < 0) tt += 1;
    if (tt > 1) tt -= 1;
    if (tt < 1 / 6) return p + (q - p) * 6 * tt;
    if (tt < 1 / 2) return q;
    if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6;
    return p;
  };
  return { r: hue(h + 1 / 3) * 255, g: hue(h) * 255, b: hue(h - 1 / 3) * 255 };
}

/**
 * Couleur de hover dérivée : on éclaircit une couleur foncée, on assombrit une
 * couleur claire (delta de luminosité fixe). Reproduit l'esprit du défaut
 * (#FF4530, plus clair que #ED2E1C). Renvoie l'entrée telle quelle si invalide.
 */
export function deriveHover(hex: string): string {
  const norm = normalizeHex(hex);
  if (!norm) return hex;
  const { h, s, l } = rgbToHsl(hexToRgb(norm));
  const delta = 0.08;
  const nl = l < 0.55 ? Math.min(1, l + delta) : Math.max(0, l - delta);
  return rgbToHex(hslToRgb(h, s, nl));
}

/**
 * Applique (ou retire) la couleur de marque sur l'élément racine via 4 CSS vars
 * inline. `null` / invalide / égale au défaut → on RETIRE les overrides → retour
 * aux valeurs du thème (globals.css). À appeler depuis un effet (ThemeProvider).
 */
export function applyBrandColor(root: HTMLElement, hex: string | null): void {
  const norm = hex ? normalizeHex(hex) : null;
  if (!norm || norm === DEFAULT_BRAND) {
    for (const v of BRAND_VARS) root.style.removeProperty(v);
    forcerLeRepeint(root);
    return;
  }
  root.style.setProperty("--color-mark", norm);
  root.style.setProperty("--color-mark-hover", deriveHover(norm));
  root.style.setProperty("--color-mark-fg", contrastFg(norm));
  root.style.setProperty("--cockpit-corner-color", norm);
  forcerLeRepeint(root);
}

/**
 * 🩸 Un élément en `transition` ne relit PAS une variable CSS qui change.
 *
 * Mesuré le 28-08-2026 sur dozka.app : après avoir choisi l'orange dans
 * « Personnaliser le thème », `--color-mark` valait bien `#f97316` sur `<html>`
 * ET sur le bouton — mais le bouton restait PEINT en `#ed2e1c`. Un `<div
 * class="bg-mark">` posé à côté, lui, était orange ; un CLONE du bouton aussi.
 * Seul l'original gardait l'ancienne couleur, jusqu'au premier repaint (un
 * survol, un scroll, un re-rendu React) qui le rattrapait.
 *
 * En cause : `.transition` de Tailwind couvre `background-color`, et Chrome
 * garde la valeur transitionnée tant que l'élément n'est pas invalidé. Tous les
 * boutons du cockpit portent cette classe : changer de couleur d'accent laissait
 * donc l'ancienne teinte sur les CTA — l'endroit précis où on la regarde.
 *
 * Le correctif est le geste standard : couper les transitions le temps d'une
 * frame, forcer un recalcul de style, les rendre. Ça recolore tout d'un coup au
 * lieu de laisser des boutons en retard, et ça évite au passage que TOUT le
 * cockpit s'anime pendant 150 ms.
 */
function forcerLeRepeint(root: HTMLElement): void {
  const doc = root.ownerDocument;
  if (!doc || typeof doc.defaultView?.requestAnimationFrame !== "function") return;
  root.setAttribute("data-sans-transition", "");
  // Lecture qui force le recalcul de style AVANT de rendre les transitions.
  void root.offsetHeight;
  doc.defaultView.requestAnimationFrame(() => {
    root.removeAttribute("data-sans-transition");
  });
}
